import {
	IconAlertCircle,
	IconBell,
	IconBrandStripe,
	IconCircleCheck,
	IconDeviceDesktop,
	IconDiscount,
	IconDroplet,
	IconGavel,
	IconLink,
	IconMail,
	IconPackage,
	IconPackages,
	IconSchool,
	IconUserPlus,
	IconUsersGroup,
	IconVocabulary,
	IconWorldWww,
} from "@tabler/icons-react"
import { type FC, type ReactNode, useEffect, useState } from "react"

import { type Platform } from "@forento/core/models/platform"
import Collapse from "@forento/ui/components/Collapse"

import Button from "~/components/Button"
import { ChevronIcon } from "~/components/Icon"
import { DefaultModal, ModalButtons, ModalTitle } from "~/components/Modal"
import { usePlatform } from "~/contexts/UserContext"
import InviteUsersModal from "~/pages/user/UsersPage/InviteUsersModal"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

type TaskData = {
	publishedOfferCount: number
	hasPaidOffers: boolean
	hasFirstAcquisition: boolean
	hasWelcomeEmail: boolean
	productCount: number
	bundleCount: number
	creatorCount: number
	hasChapterDripping: boolean
	hasCommunityChannels: boolean
	hasDiscountCodes: boolean
	hasUpdatedNotifications: boolean
}
export type Task = {
	title: string
	helpLink: { label: string; dubSlug: string }
	icon: ReactNode
	onClick: string | (() => void)
	isCompleted: boolean
	isVisible?: boolean
}
type LaunchCheckStatus = "ready" | "warning" | "missing"
type LaunchCheck = {
	isOptional?: boolean
	title: string
	detail: string
	icon: ReactNode
	onClick: string | (() => void)
	status: LaunchCheckStatus
	isVisible?: boolean
}
type LaunchContext = {
	hasBranding: boolean
	hasProductContent: boolean
	hasLandingPage: boolean
	hasPaymentGateway: boolean
	hasCustomDomain: boolean
	hasDomainIssue: boolean
	hasVerifiedEmailSender: boolean
	hasLaunchOffer: boolean
}
type LaunchAction = {
	title: string
	description: string
	actionLabel: string
	onClick: string | (() => void)
}
type LaunchChecklistItem = {
	title: string
	description: string
	onClick: string | (() => void)
	icon: ReactNode
	status: LaunchCheckStatus
}

type Props = { isDismissed: boolean } & TaskData
const Onboarding: FC<Props> = ({ isDismissed, ...taskData }) => {
	const [isCollapsed, setCollapsed] = useState(isDismissed)
	const [hasCollapsedChanged, setCollapseChanged] = useState(false)

	const { mutate } = query.platform.update.useMutation({
		onError: error => console.error(error),
	})

	useEffect(() => {
		if (hasCollapsedChanged) {
			mutate({ isOnboardingDismissed: isCollapsed })
		}
	}, [mutate, isCollapsed, hasCollapsedChanged])

	return (
		<div className="flex w-full flex-col rounded-3xl bg-[linear-gradient(277deg,#e9e1ff_2%,#FFF0DD_98%)] p-4 sm:p-6">
			<div className="flex flex-wrap items-center justify-between gap-4">
				<h2 className="min-w-0 text-xl leading-none font-semibold sm:text-2xl">Launch guide</h2>
				<Button
					onClick={() => {
						setCollapsed(current => !current)
						setCollapseChanged(true)
					}}
					className="size-6 shrink-0"
				>
					<ChevronIcon className="size-6" direction={isCollapsed ? "down" : "up"} />
				</Button>
			</div>
			<Collapse isCollapsed={isCollapsed}>
				<Tasks taskData={taskData} />
			</Collapse>
		</div>
	)
}

type TasksProps = { taskData: TaskData }
const Tasks: FC<TasksProps> = ({ taskData }) => {
	const platform = usePlatform()!

	const [isProductModalOpen, setProductModalOpen] = useState(false)
	const openProductModal = () => setProductModalOpen(true)

	const tasks: Task[] = [
		{
			title: "Create your first course",
			helpLink: { label: "Learn how to create a course", dubSlug: "dash-qs-course" },
			icon: <IconVocabulary />,
			onClick: routes.course.index(),
			isCompleted: platform.content.courses,
		},
		{
			title: "Customize your academy",
			helpLink: { label: "Learn how to customize", dubSlug: "dash-qs-customize" },
			icon: <IconSchool />,
			onClick: routes.settings.customizeAcademy(),
			isCompleted: platform.logo !== null,
		},
		{
			title: "Connect a custom domain",
			helpLink: { label: "Connecting a domain", dubSlug: "dash-qs-domain" },
			icon: <IconLink />,
			onClick: routes.settings.domain.index(),
			isCompleted: !platform.domain.platform.endsWith("forento.app"),
			isVisible: platform.plan.access.customDomain,
		},
		{
			title: "Connect a payment gateway",
			helpLink: { label: "Quick start guide", dubSlug: "dash-qs-payment" },
			icon: <IconBrandStripe />,
			onClick: routes.payments.index(),
			isCompleted: Boolean(platform.paymentGateway?.isEnabled),
		},
		{
			title: "Set up a welcome email for new users",
			helpLink: { label: "Learn how to set up a welcome email", dubSlug: "dash-qs-welcome-email" },
			icon: <IconMail />,
			onClick: routes.settings.studentEmail.index(),
			isCompleted: taskData.hasWelcomeEmail,
		},
		{
			title: "Create a new product",
			helpLink: { label: "Learn how to create a product", dubSlug: "dash-qs-product" },
			icon: <IconPackage />,
			onClick: openProductModal,
			isCompleted: taskData.productCount > 1,
			isVisible: platform.content.courses,
		},
		{
			title: "Create your website",
			helpLink: { label: "Website setup guide", dubSlug: "dash-qs-website" },
			icon: <IconDeviceDesktop />,
			onClick: routes.main.websiteEditor(),
			isCompleted: platform.domain.landingPage !== null,
			isVisible: platform.plan.access.landingPage,
		},
		{
			title: "Set up notifications",
			helpLink: { label: "Learn how to set up notifications", dubSlug: "dash-qs-notifications" },
			icon: <IconBell />,
			onClick: routes.settings.notification(),
			isCompleted: taskData.hasUpdatedNotifications,
		},
		{
			title: "Invite team member",
			helpLink: { label: "Learn how to invite a team member", dubSlug: "dash-qs-invite-team-member" },
			icon: <IconUserPlus />,
			onClick: routes.settings.team(),
			isCompleted: taskData.creatorCount > 1,
			isVisible: (platform.plan.maxAdmins ?? Infinity) > 1,
		},
		{
			title: "Create a discount code",
			helpLink: { label: "Learn how to create a discount code", dubSlug: "dash-qs-discount-code" },
			icon: <IconDiscount />,
			onClick: routes.main.discountCodes(),
			isCompleted: taskData.hasDiscountCodes,
			isVisible: platform.paymentGateway?.type === "stripe",
		},
		{
			title: "Set up the EU withdrawal function",
			helpLink: { label: "Learn about the withdrawal function", dubSlug: "dash-qs-withdrawal" },
			icon: <IconGavel />,
			onClick: routes.settings.legal(),
			isCompleted: platform.contractWithdrawalEnableDate !== null,
			isVisible: Boolean(platform.paymentGateway?.isEnabled),
		},
		{
			title: "Set up your community",
			helpLink: { label: "Learn how to set up a community", dubSlug: "dash-qs-community" },
			icon: <IconUsersGroup />,
			onClick: routes.community.index(),
			isCompleted: platform.community !== null,
			isVisible: platform.plan.access.community,
		},
		{
			title: "Create a bundle",
			helpLink: { label: "Learn how to create a bundle", dubSlug: "dash-qs-bundle" },
			icon: <IconPackages />,
			onClick: routes.bundle.index(),
			isCompleted: taskData.bundleCount > 1,
			isVisible: platform.plan.access.bundles,
		},
		{
			title: "Set up content dripping",
			helpLink: { label: "Learn how to set up content dripping", dubSlug: "dash-qs-drip" },
			icon: <IconDroplet />,
			onClick: routes.course.index(),
			isCompleted: taskData.hasChapterDripping,
			isVisible: platform.plan.access.chapterDripping,
		},
		{
			title: "Organize your community",
			helpLink: { label: "Learn how to organize your community", dubSlug: "dash-qs-community-organize" },
			icon: <IconUsersGroup />,
			onClick: routes.community.index(),
			isCompleted: taskData.hasCommunityChannels,
			isVisible: platform.plan.access.communityChannels,
		},
	]

	const visibleTasks = tasks.filter(task => task.isVisible ?? true)
	const context = getLaunchContext(platform, taskData)
	const launchChecks = getLaunchChecks(platform, taskData, context)
	const { essentialItems, optionalItems } = getLaunchChecklistItems(visibleTasks, launchChecks)

	return (
		<>
			<DefaultModal onClose={() => setProductModalOpen(false)} isOpen={isProductModalOpen}>
				<ModalTitle>Create a new product</ModalTitle>
				<ModalButtons $vertical>
					<Button variant="secondary" onClick={routes.course.index()}>
						Course
					</Button>
					<Button variant="secondary" onClick={routes.exam.index()}>
						Exam
					</Button>
					<Button variant="secondary" onClick={routes.video.create()}>
						Video
					</Button>
					<Button variant="secondary" onClick={routes.downloadable.create()}>
						Downloadable
					</Button>
					<Button variant="secondary" onClick={routes.sessions.create()}>
						Session
					</Button>
					<Button variant="secondary" onClick={routes.main.event()}>
						Event
					</Button>
					<Button variant="secondary" onClick={routes.bundle.index()}>
						Bundle
					</Button>
					<Button variant="primary" onClick={() => setProductModalOpen(false)}>
						Close
					</Button>
				</ModalButtons>
			</DefaultModal>
			<div className="flex flex-col gap-4 pt-4">
				<LaunchGuidance
					platform={platform}
					taskData={taskData}
					context={context}
					checks={launchChecks.filter(check => !check.isOptional)}
					onCreateProduct={openProductModal}
				/>
				<LaunchChecklist
					title="Launch essentials"
					description="The checks counted in the readiness bar above — finish these before you launch."
					items={essentialItems}
				/>
				<LaunchChecklist
					title="Optional setup"
					description="Worth doing, but none of these hold up your launch."
					items={optionalItems}
				/>
			</div>
		</>
	)
}

type LaunchGuidanceProps = {
	platform: Platform
	taskData: TaskData
	context: LaunchContext
	checks: LaunchCheck[]
	onCreateProduct: () => void
}
const LaunchGuidance: FC<LaunchGuidanceProps> = ({ platform, taskData, context, checks, onCreateProduct }) => {
	const [isInviteOpen, setInviteOpen] = useState(false)
	const readyCount = checks.filter(check => check.status === "ready").length
	const readinessScore = Math.round((readyCount / checks.length) * 100)
	const nextStep = getNextStep(platform, taskData, context, onCreateProduct, () => setInviteOpen(true))

	return (
		<div className={panelClassName}>
			<InviteUsersModal isOpen={isInviteOpen} onClose={() => setInviteOpen(false)} />
			<div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
				<p className={eyebrowClassName}>Launch readiness</p>
				<p className="text-sm leading-tight font-semibold">
					{readyCount} of {checks.length} launch essentials ready
				</p>
			</div>
			<div className="h-2 overflow-hidden rounded-full bg-[#f1f1f1]">
				<div
					className="bg-primary h-full rounded-full transition-[width] duration-200"
					style={{ width: `${readinessScore}%` }}
				/>
			</div>
			<div className="flex items-start gap-2 rounded-lg bg-gray-50 p-3">
				{taskData.hasFirstAcquisition && <IconCircleCheck className="text-success size-5 shrink-0" />}
				<div>
					<p className="text-sm font-semibold">
						{taskData.hasFirstAcquisition
							? "First enrollment or purchase achieved!"
							: "Next milestone: first enrollment or purchase"}
					</p>
					<p className="text-light text-sm">
						{taskData.hasFirstAcquisition
							? "Your first student has started learning or purchased an offer. Keep sharing your launch."
							: "Completed automatically when a student starts a course or completes a purchase or membership signup. Creator and test accounts do not count."}
					</p>
				</div>
			</div>
			<div className="border-component-border mt-1 flex flex-col gap-3 border-t pt-4">
				<p className={eyebrowClassName}>Suggested next step</p>
				<h3 className="text-base leading-snug font-semibold">{nextStep.title}</h3>
				<p className="text-light text-sm leading-normal font-medium">{nextStep.description}</p>
				<Button className="w-fit max-w-full" variant="primary" onClick={nextStep.onClick}>
					{nextStep.actionLabel}
				</Button>
			</div>
		</div>
	)
}

type LaunchChecklistProps = { title: string; description: string; items: LaunchChecklistItem[] }
const LaunchChecklist: FC<LaunchChecklistProps> = ({ title, description, items }) => {
	const pendingItems = items.filter(item => item.status !== "ready")
	const completedItems = items.filter(item => item.status === "ready")

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-col gap-1">
				<h3 className="text-base leading-tight font-semibold">{title}</h3>
				<p className="text-light text-sm leading-normal font-medium">{description}</p>
			</div>
			{pendingItems.length > 0 && (
				<div className="flex flex-wrap gap-4">
					{pendingItems.map(item => (
						<Button
							key={item.title}
							className="flex min-w-[min(100%,280px)] flex-[1_1_280px] items-center gap-3 rounded-lg bg-white p-4 text-black"
							onClick={item.onClick}
						>
							{item.status === "warning" ? (
								<IconAlertCircle className="size-6 shrink-0 text-[#b86b00]" />
							) : (
								<div className="text-light shrink-0 [&>svg]:size-6">{item.icon}</div>
							)}
							<div className="min-w-0 flex-1">
								<p className="text-base leading-snug font-semibold">{item.title}</p>
								<p className="text-light mt-1 text-sm leading-normal font-medium">{item.description}</p>
							</div>
							<ChevronIcon className="size-6 shrink-0" />
						</Button>
					))}
				</div>
			)}
			{completedItems.length > 0 && (
				<div className="flex flex-wrap gap-2">
					{completedItems.map(item => (
						<Button
							key={item.title}
							className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-sm leading-tight font-semibold text-black/60"
							onClick={item.onClick}
						>
							<IconCircleCheck className="text-success size-4 shrink-0" />
							{item.title}
						</Button>
					))}
				</div>
			)}
		</div>
	)
}

function getLaunchContext(platform: Platform, taskData: TaskData): LaunchContext {
	const hasCustomDomain = platform.domain.hasCustomDomain || !platform.domain.platform.endsWith("forento.app")

	return {
		hasBranding: platform.logo !== null,
		hasProductContent: taskData.publishedOfferCount > 0,
		hasLandingPage: platform.domain.landingPage !== null,
		hasPaymentGateway: Boolean(platform.paymentGateway?.isEnabled),
		hasCustomDomain,
		hasDomainIssue: platform.domain.hasUnconfiguredDnsRecords || platform.domain.isLegacyDnsHost,
		hasVerifiedEmailSender: !platform.hasLegacyEmailSender && (platform.customEmailSender?.isVerified ?? true),
		hasLaunchOffer: taskData.hasDiscountCodes,
	}
}

function getLaunchChecks(platform: Platform, taskData: TaskData, context: LaunchContext): LaunchCheck[] {
	const productTotal = taskData.publishedOfferCount
	const paymentGatewayLabel =
		platform.paymentGateway?.type === "stripe"
			? "Stripe"
			: platform.paymentGateway?.type === "paypal"
				? "PayPal"
				: null
	const checks: LaunchCheck[] = [
		{
			title: "Published offer",
			detail: context.hasProductContent
				? `${productTotal} published ${productTotal === 1 ? "offer is" : "offers are"} available to students.`
				: "Publish an offer students can access before sending traffic to your academy.",
			icon: <IconPackage />,
			onClick: routes.course.index(),
			status: context.hasProductContent ? "ready" : "missing",
		},
		{
			title: "Academy branding",
			isOptional: true,
			detail: context.hasBranding
				? "Your logo is ready for the launch page, checkout, and emails."
				: "Add your logo so the launch feels recognizable across the full flow.",
			icon: <IconSchool />,
			onClick: routes.settings.customizeAcademy(),
			status: context.hasBranding ? "ready" : "missing",
		},
		{
			title: "Launch page",
			detail: context.hasLandingPage
				? `Your website is available at ${platform.domain.landingPage}.`
				: "Create a website page so visitors have a focused place to decide.",
			icon: <IconWorldWww />,
			onClick: routes.main.websiteEditor(),
			status: context.hasLandingPage ? "ready" : "missing",
			isVisible: platform.plan.access.landingPage,
		},
		{
			title: "Domain",
			isOptional: true,
			detail: getDomainDetail(platform, context),
			icon: <IconLink />,
			onClick: routes.settings.domain.index(),
			status: getDomainStatus(platform, context),
		},
		{
			title: "Payments",
			isVisible: taskData.hasPaidOffers,
			detail: context.hasPaymentGateway
				? `${paymentGatewayLabel} checkout is connected.`
				: "Connect Stripe or PayPal before announcing a paid launch.",
			icon: <IconBrandStripe />,
			onClick: routes.payments.index(),
			status: context.hasPaymentGateway ? "ready" : "missing",
		},
		{
			title: "Email",
			detail: getEmailDetail(platform, taskData, context),
			icon: <IconMail />,
			onClick: getEmailAction(platform, taskData, context),
			status: getEmailStatus(taskData, context),
		},
		{
			title: "Launch offer",
			isOptional: true,
			detail: context.hasLaunchOffer
				? "Your launch discount is ready to use in the first campaign."
				: "Create a first-week discount if you want a clear reason to buy now.",
			icon: <IconDiscount />,
			onClick: routes.main.discountCodes(),
			status: context.hasLaunchOffer ? "ready" : "missing",
			isVisible: platform.paymentGateway?.type === "stripe",
		},
	]

	return checks.filter(check => check.isVisible ?? true)
}

const duplicatedLaunchTaskTitles = new Set([
	"Create your first course",
	"Customize your academy",
	"Connect a custom domain",
	"Connect a payment gateway",
	"Set up a welcome email for new users",
	"Create a new product",
	"Create your website",
	"Create a discount code",
])

function getLaunchChecklistItems(
	tasks: Task[],
	launchChecks: LaunchCheck[],
): { essentialItems: LaunchChecklistItem[]; optionalItems: LaunchChecklistItem[] } {
	const toChecklistItem = (check: LaunchCheck): LaunchChecklistItem => ({
		title: check.title,
		description: check.detail,
		icon: check.icon,
		onClick: check.onClick,
		status: check.status,
	})
	const essentialItems = launchChecks.filter(check => !check.isOptional).map(toChecklistItem)
	const optionalChecks = launchChecks.filter(check => check.isOptional).map(toChecklistItem)
	const optionalItems = tasks
		.filter(task => !duplicatedLaunchTaskTitles.has(task.title))
		.map(task => ({
			title: task.title,
			description: task.helpLink.label,
			icon: task.icon,
			onClick: task.onClick,
			status: task.isCompleted ? ("ready" as const) : ("missing" as const),
		}))

	return { essentialItems, optionalItems: [...optionalChecks, ...optionalItems] }
}

function getDomainDetail(platform: Platform, context: LaunchContext): string {
	if (platform.domain.hasUnconfiguredDnsRecords) {
		return "DNS records still need attention before your custom domain is fully connected."
	}

	if (platform.domain.isLegacyDnsHost) {
		return "Your domain uses a legacy DNS host and should be updated before launch."
	}

	if (context.hasCustomDomain) {
		return `Custom domain connected: ${platform.domain.platform}.`
	}

	if (!platform.plan.access.customDomain) {
		return `Your Forento domain is ready: ${platform.domain.platform}.`
	}

	return "Connect a custom domain, or plan to launch on your Forento domain first."
}

function getDomainStatus(platform: Platform, context: LaunchContext): LaunchCheckStatus {
	if (context.hasDomainIssue) return "warning"
	if (context.hasCustomDomain || !platform.plan.access.customDomain) return "ready"
	return "missing"
}

function getEmailDetail(platform: Platform, taskData: TaskData, context: LaunchContext): string {
	if (platform.hasLegacyEmailSender) {
		return "Update your legacy sender so launch emails keep strong deliverability."
	}

	if (!context.hasVerifiedEmailSender) {
		return "Your custom sender is connected, but still waiting for verification."
	}

	if (taskData.hasWelcomeEmail) {
		return platform.customEmailSender?.isVerified
			? `Welcome email is enabled and ${platform.customEmailSender.domain} is verified.`
			: "Welcome email is enabled and the default sender can deliver it."
	}

	return "Turn on the welcome email so new students hear from you right away."
}

function getEmailAction(platform: Platform, taskData: TaskData, context: LaunchContext): string {
	if (platform.hasLegacyEmailSender || !context.hasVerifiedEmailSender) return routes.settings.customizeAcademy()
	if (!taskData.hasWelcomeEmail) return routes.settings.studentEmail.index()
	return routes.settings.studentEmail.index()
}

function getEmailStatus(taskData: TaskData, context: LaunchContext): LaunchCheckStatus {
	if (!context.hasVerifiedEmailSender) return "warning"
	if (taskData.hasWelcomeEmail) return "ready"
	return "missing"
}

function getNextStep(
	platform: Platform,
	taskData: TaskData,
	context: LaunchContext,
	onCreateProduct: () => void,
	onInviteStudents: () => void,
): LaunchAction {
	if (!context.hasProductContent) {
		return {
			title: "Publish an accessible offer",
			description: "Create or finish a product, publish it, and make it available for free or purchase.",
			actionLabel: "Choose product type",
			onClick: onCreateProduct,
		}
	}

	if (platform.plan.access.landingPage && !context.hasLandingPage) {
		return {
			title: "Build the launch page",
			description:
				"Give visitors one public page with the product, outcome, and next action before you share the link.",
			actionLabel: "Open website editor",
			onClick: routes.main.websiteEditor(),
		}
	}

	if (taskData.hasPaidOffers && !context.hasPaymentGateway) {
		return {
			title: "Connect checkout",
			description: "A paid launch needs payment connected before you send buyers to the offer.",
			actionLabel: "Connect payments",
			onClick: routes.payments.index(),
		}
	}

	if (!taskData.hasWelcomeEmail || !context.hasVerifiedEmailSender) {
		return {
			title: "Check the first email",
			description: "Make sure new students get a welcome email and that your sender is ready for launch traffic.",
			actionLabel: "Open email settings",
			onClick: getEmailAction(platform, taskData, context),
		}
	}

	if (!platform.customEmailSender?.isVerified) {
		return {
			title: "Set up student invitations",
			description: "Connect and verify a custom email sender to invite your first students.",
			actionLabel: "Set up email sender",
			onClick: routes.settings.customizeAcademy(),
		}
	}

	return {
		title: taskData.hasFirstAcquisition ? "Keep your audience growing" : "Invite your first students",
		description:
			"Your essentials look ready. Invite a small group of students to explore your academy and try your offer.",
		actionLabel: "Invite students",
		onClick: onInviteStudents,
	}
}

const panelClassName = "flex min-w-[min(100%,260px)] flex-col gap-3 rounded-lg bg-white p-4"
const eyebrowClassName = "font-semibold text-light text-sm leading-tight"

export default Onboarding
